// Calendario de obra + "Entregas detectadas": todo se calcula en el navegador con las mismas
// detecciones que ya carga el Dashboard IA (sin costo extra de IA ni de servidores).
//
// Visita de camión = un Camión / Camión volquete / Mixer (opcional: Camioneta) que aparece en 2 o más
// fotos seguidas más o menos en el mismo lugar (IoU > 0,3). Las fotos seguidas se juntan en un solo evento
// con hora de llegada (primera foto), salida (última), duración, tipo de vehículo y la foto de antes y de después.
// Un vehículo que está casi todo el día en el mismo lugar es un vehículo estacionado, no una entrega.
import { photoStats, dayKey, fmtHM, fmtKey, fmtDow, photoUrl } from './iaData';
import { titulo as tituloV3 } from './entregasV3';

export const TRUCKS = ['Camion', 'Camion volquete', 'Mixer'];
export const VEHICLE_LABEL = { Camion: 'camión', 'Camion volquete': 'camión volquete', Mixer: 'mixer', Camioneta: 'camioneta' };
export const MACHINES_SEEN = [
  { key: 'excavadora', classes: ['Excavadora', 'Retroexcavadora'], label: 'Excavadora', icon: '🚜' },
  { key: 'mixer', classes: ['Mixer'], label: 'Mixer', icon: '🌀' },
  { key: 'camion', classes: ['Camion', 'Camion volquete'], label: 'Camión', icon: '🚚' },
];

export const DELIV = {
  minConf: 0.4, // confianza mínima de la caja del vehículo
  iou: 0.3, // mismo lugar entre fotos
  maxGapMin: 22, // minutos sin ver el vehículo que se toleran dentro de una misma visita (una caja que el modelo no vio)
  minSpanMin: 5, // y tiene que estar al menos 5 minutos (en ráfagas de fotos, uno que solo pasa sale en 2 fotos seguidas)
  minPhotos: 2, // fotos seguidas para contar como visita
  parkedShare: 0.8, // presente en más del 80% de las fotos del día = estacionado
  parkedHours: 6, // o más de 6 horas quieto
  spotShare: 0.4, // si en ese lugar se "ve" un camión en el 40% de las fotos del día (aunque sea salteado) = algo fijo
  spotMonth: 0.12, // o si en todo el mes se "ve" un camión en ese lugar en el 12% de las fotos = algo fijo (un obrador, una hormigonera).
  // Ojo: no se usa "cuántos días aparece", porque los camiones de verdad suelen descargar siempre en el mismo portón.
};

export const iou = (a, b) => {
  const ix = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
  const iy = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
  const inter = ix * iy;
  const u = (a.x2 - a.x1) * (a.y2 - a.y1) + (b.x2 - b.x1) * (b.y2 - b.y1) - inter;
  return u > 0 ? inter / u : 0;
};

// Agrupa las fotos (ya filtradas por jornada) por día de obra
export function byDay(photos) {
  const m = new Map();
  for (const p of photos) {
    const k = dayKey(p.ms);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(p);
  }
  return m;
}

// Visitas de vehículos de un día. photos ordenadas por hora.
export function dayVisits(photos, cfg, { withPickups = false } = {}) {
  const classes = new Set(withPickups ? [...TRUCKS, 'Camioneta'] : TRUCKS);
  const tracks = [];
  let open = [];
  photos.forEach((p, i) => {
    if (!p.hasBoxes) return;
    const dets = p.dets.filter((d) => classes.has(d.cls) && d.x1 != null && d.conf >= Math.max(DELIV.minConf, cfg.thr?.[d.cls] ?? 0));
    const used = new Set();
    // Cada vehículo de esta foto se pega a la visita abierta con la que más se superpone
    const pairs = [];
    dets.forEach((d, di) => open.forEach((t, ti) => {
      const v = iou(d, t.last);
      if (v > DELIV.iou) pairs.push([v, di, ti]);
    }));
    pairs.sort((a, b) => b[0] - a[0]);
    const takenT = new Set();
    for (const [, di, ti] of pairs) {
      if (used.has(di) || takenT.has(ti)) continue;
      used.add(di); takenT.add(ti);
      const t = open[ti];
      t.idx.push(i); t.boxes.push(dets[di]); t.last = dets[di];
    }
    dets.forEach((d, di) => {
      if (used.has(di)) return;
      const t = { idx: [i], boxes: [d], last: d };
      tracks.push(t); open.push(t);
    });
    // Cierra las visitas que ya no se ven hace más de una foto (o mucho tiempo)
    const next = photos[i + 1];
    open = open.filter((t) => {
      const lastI = t.idx[t.idx.length - 1];
      if (!next) return false;
      // Sigue abierta si la próxima foto está a pocos minutos de la última vez que se lo vio
      // (con fotos cada 10 min tolera una foto sin el vehículo; en ráfagas de fotos tolera varias)
      return next.ms - photos[lastI].ms <= DELIV.maxGapMin * 60e3;
    });
  });

  const n = photos.length;
  const out = [];
  // Todas las cajas de camión del día (con confianza más baja) para medir cuánto está ocupado cada lugar
  const loose = photos.map((p) => (p.hasBoxes ? p.dets.filter((d) => classes.has(d.cls) && d.x1 != null && d.conf >= 0.25) : []));
  for (const t of tracks) {
    if (t.idx.length < DELIV.minPhotos) continue;
    const first = t.idx[0], last = t.idx[t.idx.length - 1];
    const arrive = photos[first].ms, leave = photos[last].ms;
    if (leave - arrive < DELIV.minSpanMin * 60e3) continue;
    // Tipo de vehículo: el que más confianza sumó en la visita (el modelo a veces duda entre camión y volquete)
    const votes = {};
    t.boxes.forEach((b) => { votes[b.cls] = (votes[b.cls] || 0) + b.conf; });
    let cls = Object.keys(votes).sort((a, b) => votes[b] - votes[a])[0];
    // Si alguna foto lo vio claramente como mixer, es un mixer
    if (t.boxes.filter((b) => b.cls === 'Mixer').length >= Math.max(1, t.boxes.length / 3)) cls = 'Mixer';
    const box = t.boxes.reduce((a, b) => (b.conf > a.conf ? b : a));
    const occ = loose.filter((ds) => ds.some((d) => iou(d, box) > DELIV.iou)).length / Math.max(1, n);
    const parked = t.idx.length >= DELIV.parkedShare * n || leave - arrive >= DELIV.parkedHours * 3600e3 || occ >= DELIV.spotShare;
    const after = last < n - 1 ? photos[last + 1] : null;
    out.push({
      id: `${dayKey(arrive)}|${fmtHM(arrive)}|${Math.round((box.x1 + box.x2) / 2)},${Math.round((box.y1 + box.y2) / 2)}`,
      day: dayKey(arrive),
      cls,
      arrive,
      leave,
      // hasta la primera foto sin el vehículo (como mucho 10 min después de la última en que se lo vio)
      mins: Math.round((Math.min(after ? after.ms : Infinity, leave + 10 * 60e3) - arrive) / 60e3),
      occ: +occ.toFixed(2),
      nPhotos: t.idx.length,
      maxConf: Math.max(...t.boxes.map((b) => b.conf)),
      box: { x1: box.x1, y1: box.y1, x2: box.x2, y2: box.y2 },
      w: photos[first].w, h: photos[first].h,
      before: first > 0 ? photos[first - 1] : null,
      arrival: photos[first],
      after,
      parked,
    });
  }
  // Une visitas cortadas: mismo tipo de vehículo, cerca y con pocos minutos sin verlo (maniobra, se corrió de lugar)
  out.sort((a, b) => a.arrive - b.arrive);
  const merged = [];
  const cx = (b) => (b.x1 + b.x2) / 2, cy = (b) => (b.y1 + b.y2) / 2, bw = (b) => b.x2 - b.x1;
  for (const e of out) {
    const prev = [...merged].reverse().find((m) => m.cls === e.cls && m.parked === e.parked && e.arrive - m.leave <= DELIV.maxGapMin * 60e3 && e.arrive >= m.arrive
      && Math.hypot(cx(m.box) - cx(e.box), cy(m.box) - cy(e.box)) < 1.5 * Math.max(bw(m.box), bw(e.box)));
    if (!prev) { merged.push(e); continue; }
    if (e.leave > prev.leave) { prev.leave = e.leave; prev.after = e.after; }
    prev.nPhotos += e.nPhotos;
    if (e.maxConf > prev.maxConf) { prev.maxConf = e.maxConf; }
    prev.mins = Math.round((Math.min(prev.after ? prev.after.ms : Infinity, prev.leave + 10 * 60e3) - prev.arrive) / 60e3);
  }
  return merged;
}

// Lugares fijos: un "camión" que aparece en el mismo lugar muchos días distintos no es una visita
// (suele ser un obrador, un contenedor o una hormigonera que el modelo confunde). Se mira el mes cargado.
export function markFixedSpots(sums) {
  const days = [...sums.values()];
  if (days.length < 5) return;
  for (const s of days) {
    for (const v of [...s.visits]) {
      let hits = 0, total = 0;
      for (const o of days) { total += o.truckBoxes.length; for (const bs of o.truckBoxes) if (bs.some((b) => iou(b, v.box) > DELIV.iou)) hits += 1; }
      v.spotMonth = +(hits / Math.max(1, total)).toFixed(3);
      if (v.spotMonth >= DELIV.spotMonth) { v.parked = true; v.fixed = true; }
    }
    s.parked = [...s.parked, ...s.visits.filter((v) => v.parked)];
    s.visits = s.visits.filter((v) => !v.parked);
  }
}

// Resumen de un día para la celda del calendario y el diario
export function daySummary(photos, cfg, opts) {
  let first = null, last = null, peak = 0, persons = 0, helmet = 0, best = null, bestScore = -1;
  const seen = new Set();
  const curve = [];
  for (const p of photos) {
    const s = photoStats(p, cfg);
    curve.push({ ms: p.ms, persons: s.persons, machines: s.machines });
    if (s.persons > 0) { if (first == null) first = p.ms; last = p.ms; }
    peak = Math.max(peak, s.persons);
    persons += s.persons; helmet += s.helmet;
    for (const d of p.dets) {
      if (d.conf < Math.max(DELIV.minConf, cfg.thr?.[d.cls] ?? 0)) continue;
      for (const m of MACHINES_SEEN) if (m.classes.includes(d.cls)) seen.add(m.key);
    }
    const score = s.persons * 2 + s.machines;
    if (score > bestScore) { bestScore = score; best = p; }
  }
  const visits = dayVisits(photos, cfg, opts);
  // Cajas de camión de cada foto (con confianza baja también) para detectar lugares fijos en el mes
  const truckBoxes = photos.map((p) => p.dets.filter((d) => TRUCKS.includes(d.cls) && d.x1 != null && d.conf >= 0.25));
  return {
    n: photos.length,
    first, last, peak,
    casco: persons ? Math.min(100, Math.round((100 * helmet) / persons)) : null,
    machines: MACHINES_SEEN.filter((m) => seen.has(m.key)),
    visits: visits.filter((v) => !v.parked),
    parked: visits.filter((v) => v.parked),
    truckBoxes,
    best: bestScore > 0 ? best : photos[Math.floor(photos.length / 2)] || null,
    curve,
  };
}

// ---------------------------------------------------------------------------
// Lo que marca el usuario: "Confirmar entrega" / "No fue entrega" + qué llegó.
// Por ahora en el navegador (localStorage), exportable a CSV.
// ---------------------------------------------------------------------------
const marksKey = (cam) => 'iaDash:entregas:' + cam;
export function loadMarks(cam) {
  try { return JSON.parse(localStorage.getItem(marksKey(cam)) || '{}'); } catch (e) { return {}; }
}
export function saveMark(cam, ev, patch) {
  const all = loadMarks(cam);
  const cur = all[ev.id] || { day: ev.day, llegada: fmtHM(ev.arrive), salida: fmtHM(ev.leave), vehiculo: VEHICLE_LABEL[ev.cls] || ev.cls };
  const next = { ...cur, ...patch, actualizado: new Date().toISOString() };
  if (next.estado == null && !next.nota) delete all[ev.id]; else all[ev.id] = next;
  try { localStorage.setItem(marksKey(cam), JSON.stringify(all)); } catch (e) { /* sin storage */ }
  return all;
}
export function marksCsv(cam) {
  const all = loadMarks(cam);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = Object.values(all).sort((a, b) => (a.day + a.llegada).localeCompare(b.day + b.llegada));
  return ['Fecha,Llegada,Salida,Vehículo,Estado,Qué llegó', ...rows.map((r) => [fmtKey(r.day), r.llegada, r.salida, r.vehiculo, r.estado === 'si' ? 'Entrega confirmada' : r.estado === 'no' ? 'No fue entrega' : 'Sin revisar', r.nota].map(esc).join(','))].join('\n');
}

// ---------------------------------------------------------------------------
// Texto del diario para mandar por WhatsApp o mail
// ---------------------------------------------------------------------------
const plural = (n, s, p) => (n === 1 ? s : p);
export function diarioText(name, key, s, wx, marks) {
  const bits = [];
  if (s.first != null) bits.push(`${fmtHM(s.first)}–${fmtHM(s.last)}`);
  else bits.push('sin personas en obra');
  if (s.peak) bits.push(`pico ${s.peak} ${plural(s.peak, 'persona', 'personas')}`);
  if (s.casco != null) bits.push(`${s.casco}% con casco`);
  const vis = s.visits.filter((v) => marks[v.id]?.estado !== 'no');
  vis.forEach((v) => {
    const m = marks[v.id];
    const que = m?.nota ? ` (${m.nota})` : '';
    if (v.v3) { const t = tituloV3(v); bits.push(`${t.charAt(0).toLowerCase()}${t.slice(1)} ${fmtHM(v.arrive)}${v.tipo === 'material' ? '' : `–${fmtHM(v.leave)}`}${que}`); return; }
    bits.push(`llegó un ${VEHICLE_LABEL[v.cls] || v.cls} ${fmtHM(v.arrive)}–${fmtHM(v.leave)}${que}`);
  });
  if (wx) bits.push(wx.p >= 0.1 ? `lluvia ${String(+wx.p.toFixed(1)).replace('.', ',')} mm` : 'sin lluvia');
  const head = `Diario de obra ${fmtDow(key)} ${key.slice(8, 10)}/${key.slice(5, 7)}${name ? ` · ${name}` : ''}`;
  return `${head}: ${bits.join(', ')}.`;
}

// ---------------------------------------------------------------------------
// Señal visual barata: ¿cambió la zona donde estuvo el camión? (antes vs. después)
// Compara en escala de grises, en chico, la zona de la caja agrandada ×2, descontando el cambio general
// de luz de toda la foto. Necesita poder leer los píxeles: en local pasa por el proxy /s3img; en producción
// depende de que el bucket mande CORS (si no, devuelve null y no se muestra nada).
// ---------------------------------------------------------------------------
export const loadImg = (src) => new Promise((ok, fail) => {
  const im = new Image();
  im.crossOrigin = 'anonymous';
  im.onload = () => ok(im);
  im.onerror = fail;
  im.src = src;
});
const gray = (im, sx, sy, sw, sh, W, H) => {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(im, sx, sy, sw, sh, 0, 0, W, H);
  const d = ctx.getImageData(0, 0, W, H).data; // tira error si la imagen no permite leer píxeles
  const g = new Float32Array(W * H);
  let sum = 0;
  for (let i = 0; i < g.length; i++) { g[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2]; sum += g[i]; }
  const mean = sum / g.length;
  for (let i = 0; i < g.length; i++) g[i] -= mean; // descuenta brillo general (nube que pasa, sol)
  return g;
};
const mad = (a, b) => { let s = 0; for (let i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]); return s / a.length; };

const diffCache = new Map();
export function zoneChange(ev) {
  if (!ev.before) return Promise.resolve(null);
  if (diffCache.has(ev.id)) return diffCache.get(ev.id);
  const p = (async () => {
    const [ua, ub, uc] = await Promise.all([photoUrl(ev.before), ev.after ? photoUrl(ev.after) : null, photoUrl(ev.arrival)]);
    const [a, b, c] = await Promise.all([loadImg(ua), ub ? loadImg(ub) : null, loadImg(uc)]);
    const sx = a.naturalWidth / (ev.w || a.naturalWidth), sy = a.naturalHeight / (ev.h || a.naturalHeight);
    const cx = ((ev.box.x1 + ev.box.x2) / 2) * sx, cy = ((ev.box.y1 + ev.box.y2) / 2) * sy;
    const bw = Math.min(a.naturalWidth, (ev.box.x2 - ev.box.x1) * sx * 2), bh = Math.min(a.naturalHeight, (ev.box.y2 - ev.box.y1) * sy * 2);
    const x = Math.max(0, Math.min(a.naturalWidth - bw, cx - bw / 2)), y = Math.max(0, Math.min(a.naturalHeight - bh, cy - bh / 2));
    const W = a.naturalWidth, H = a.naturalHeight;
    const gWhole = (im) => gray(im, 0, 0, W, H, 96, 54);
    // ¿Llegó algo? La caja del vehículo (un poco agrandada) en la foto de llegada contra la de antes
    const tw = Math.min(W, (ev.box.x2 - ev.box.x1) * sx * 1.3), th = Math.min(H, (ev.box.y2 - ev.box.y1) * sy * 1.3);
    const tx = Math.max(0, Math.min(W - tw, cx - tw / 2)), ty = Math.max(0, Math.min(H - th, cy - th / 2));
    const tight = mad(gray(a, tx, ty, tw, th, 32, 32), gray(c, tx, ty, tw, th, 32, 32));
    const wholeArr = mad(gWhole(a), gWhole(c));
    const out = { tight: +tight.toFixed(1), wholeArr: +wholeArr.toFixed(1), arrived: tight > 10 && tight > wholeArr * 1.5 };
    if (b) {
      // ¿Quedó algo? La zona (caja ×2) después de que se fue contra antes de que llegara, descontando el cambio general de la foto
      const zone = mad(gray(a, x, y, bw, bh, 48, 48), gray(b, x, y, bw, bh, 48, 48));
      const whole = mad(gWhole(a), gWhole(b));
      Object.assign(out, { zone: +zone.toFixed(1), whole: +whole.toFixed(1), changed: zone > 12 && zone > whole * 1.8 });
    }
    return out;
  })().catch(() => null);
  diffCache.set(ev.id, p);
  return p;
}

